import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { useData } from '../data/DataProvider';
import { applyTenantBranding } from '../theme/tokens';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import EngineerShell from './EngineerShell';

// Resolves the engineer session before rendering the engineer portal.
// Handles the three states: resolving (loading), ok (engineer portal),
// error (invalid credentials).
export default function EngineerAuthGate() {
  const { tenantSlug } = useParams();
  const { ready, data } = useData();
  const navigate = useNavigate();
  const [state, setState] = useState('resolving');
  const [errorMsg, setErrorMsg] = useState('');

  const engineerSession = pbEngineer.authStore.record;
  const hasEngineerSession = Boolean(
    pbEngineer.authStore.isValid && engineerSession && engineerSession.role === 'engineer'
  );

  useEffect(() => {
    // Session route (no slug): already authenticated engineer → straight in.
    if (!tenantSlug) {
      if (hasEngineerSession) {
        setState('ok');
      } else {
        // Not signed in → engineer login screen
        navigate('/engineer/login', { replace: true });
      }
      return;
    }

    // Already signed in: keep the live session
    if (hasEngineerSession) {
      // Check tenant matches
      if (engineerSession.tenant_id && !ready) return;
      if (engineerSession.tenant_id && data.tenant.id !== engineerSession.tenant_id) {
        setErrorMsg('Engineer account does not belong to this tenant');
        setState('error');
        return;
      }
      
      setState('ok');
      return;
    }

    // No session: redirect to login with tenant slug
    if (ready) {
      navigate(`/engineer/login/${tenantSlug}`, { replace: true });
    }
    setState('error');
  }, [tenantSlug, hasEngineerSession, navigate, ready, data]);

  useEffect(() => {
    if (state === 'ok' && ready) {
      applyTenantBranding({
        primary: data.tenant.primary_color || '#173e36',
        primaryStrong: data.tenant.primary_color || '#173e36',
        accent: data.tenant.accent_color || '#dfff73',
      });
    }
  }, [state, ready, data]);

  if (state === 'resolving') {
    return <div className="standalone-page"><Skeleton rows={5} /></div>;
  }

  if (state === 'error') {
    return (
      <div className="standalone-page">
        <EmptyState
          title="Access Denied"
          message={errorMsg || 'You need to sign in to access the engineer portal'}
          action="Go to Login"
          onAction={() => navigate(`/engineer/login${tenantSlug ? `/${tenantSlug}` : ''}`, { replace: true })}
        />
      </div>
    );
  }

  // No valid session: bounce to login (prevent admin login redirect)
  if (!hasEngineerSession) {
    // Use window.location.hash to stay within engineer portal
    const engineerLoginPath = `/engineer/login${tenantSlug ? `/${tenantSlug}` : ''}`;
    window.location.hash = engineerLoginPath;
    return null; // Return null to prevent rendering anything before redirect
  }

  if (!ready) {
    return <div className="standalone-page"><Skeleton rows={6} /></div>;
  }

  return <EngineerShell />;
}